import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Save, Camera, Upload, AlertTriangle, Calendar, Wrench, MapPin, Clock, Package } from 'lucide-react';
import { supabase } from '@/lib/supabaseClient';
import CameraCapture from './CameraCapture';

const OutilForm = ({ outil, onClose, onSave, emplacementId }) => {
  const [loading, setLoading] = useState(false);
  const [showCamera, setShowCamera] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  
  const [categories, setCategories] = useState([]);
  const [emplacements, setEmplacements] = useState([]);
  
  const [formData, setFormData] = useState({
    nom: '',
    reference: '',
    categorie_id: '',
    emplacement_id: emplacementId || '',
    quantite: 1,
    etat: 'complet',
    disponible: true,
    photo_url: '',
    observations: '',
    necessite_controle: false,
    periodicite_controle_mois: '',
    date_dernier_controle: '',
    necessite_remplacement: false,
    duree_vie_mois: '',
    date_mise_en_service: '',
    observations_maintenance: ''
  });

  useEffect(() => {
    chargerDonnees();
    if (outil) {
      setFormData({
        nom: outil.nom ?? '',
        reference: outil.reference ?? '',
        categorie_id: outil.categorie_id ?? '',
        emplacement_id: outil.emplacement_id ?? emplacementId ?? '',
        quantite: outil.quantite ?? 1,
        etat: outil.etat ?? 'complet',
        disponible: outil.disponible ?? true,
        photo_url: outil.photo_url ?? '',
        observations: outil.observations ?? '',
        necessite_controle: outil.necessite_controle ?? false,
        periodicite_controle_mois: outil.periodicite_controle_mois ?? '',
        date_dernier_controle: outil.date_dernier_controle ?? '',
        necessite_remplacement: outil.necessite_remplacement ?? false,
        duree_vie_mois: outil.duree_vie_mois ?? '',
        date_mise_en_service: outil.date_mise_en_service ?? '',
        observations_maintenance: outil.observations_maintenance ?? ''
      });
    }
  }, [outil, emplacementId]);

  const chargerDonnees = async () => {
    try {
      const { data: cats } = await supabase
        .from('mat_categories')
        .select('*')
        .order('nom');
      
      if (cats) setCategories(cats);

      const { data: emps } = await supabase
        .from('mat_emplacements')
        .select(`
          id,
          nom,
          type_emplacement,
          mat_ateliers (code, nom)
        `)
        .order('nom');
      
      if (emps) {
        setEmplacements(emps.map(e => ({
          ...e,
          atelier_code: e.mat_ateliers?.code || '',
          atelier_nom: e.mat_ateliers?.nom || ''
        })));
      }
    } catch (error) {
      console.error('Erreur chargement:', error);
    }
  };

  const handlePhotoCapture = async (photoUrl) => {
    setFormData(prev => ({ ...prev, photo_url: photoUrl }));
    setShowCamera(false);
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    try {
      setUploadingPhoto(true);
      
      const fileExt = file.name.split('.').pop();
      const fileName = `${Date.now()}.${fileExt}`;
      const filePath = `outils/${fileName}`;

      const { error: uploadError } = await supabase.storage
        .from('outils-photos')
        .upload(filePath, file);

      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage
        .from('outils-photos')
        .getPublicUrl(filePath);

      setFormData(prev => ({ ...prev, photo_url: publicUrl }));
    } catch (error) {
      console.error('Erreur upload:', error);
      alert('Erreur upload photo');
    } finally {
      setUploadingPhoto(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.nom || !formData.categorie_id || !formData.emplacement_id) {
      alert('Champs obligatoires manquants');
      return;
    }

    setLoading(true);

    try {
      const outilData = {
        nom: formData.nom,
        reference: formData.reference || null,
        categorie_id: formData.categorie_id,
        emplacement_id: formData.emplacement_id,
        quantite: parseInt(formData.quantite),
        etat: formData.etat,
        disponible: formData.disponible,
        photo_url: formData.photo_url || null,
        observations: formData.observations || null,
        necessite_controle: formData.necessite_controle,
        periodicite_controle_mois: formData.necessite_controle && formData.periodicite_controle_mois 
          ? parseInt(formData.periodicite_controle_mois) 
          : null,
        date_dernier_controle: formData.necessite_controle && formData.date_dernier_controle 
          ? formData.date_dernier_controle 
          : null,
        necessite_remplacement: formData.necessite_remplacement,
        duree_vie_mois: formData.necessite_remplacement && formData.duree_vie_mois 
          ? parseInt(formData.duree_vie_mois) 
          : null,
        date_mise_en_service: formData.necessite_remplacement && formData.date_mise_en_service 
          ? formData.date_mise_en_service 
          : null,
        observations_maintenance: formData.observations_maintenance || null
      };

      if (outil) {
        const { error } = await supabase
          .from('mat_outils')
          .update(outilData)
          .eq('id', outil.id)
          .select();
        
        if (error) throw error;
      } else {
        const { error } = await supabase
          .from('mat_outils')
          .insert([outilData])
          .select();
        
        if (error) throw error;
      }

      if (onSave) onSave();
      onClose();
    } catch (error) {
      console.error('Erreur sauvegarde:', error);
      alert('Erreur: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const emplacementsGroupes = emplacements.reduce((acc, emp) => {
    const type = emp.type_emplacement || 'autre';
    if (!acc[type]) acc[type] = [];
    acc[type].push(emp);
    return acc;
  }, {});

  const typeLabels = {
    armoire: '📦 Armoires',
    zone: '🏗️ Zones libres',
    fixe: '🔧 Équipements fixes',
    etabli: '🪛 Établis',
    mobile: '🚛 Équipements mobiles',
    autre: '📍 Autres'
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4"
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        className="bg-slate-900 rounded-xl max-w-4xl w-full max-h-[90vh] flex flex-col"
      >
        {/* Header */}
        <div className="bg-slate-800 px-6 py-4 flex items-center justify-between rounded-t-xl flex-shrink-0">
          <h2 className="text-2xl font-bold text-white flex items-center gap-2">
            <Wrench className="w-7 h-7 text-purple-400" />
            {outil ? 'Modifier' : 'Nouvel outil'}
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-white transition-colors">
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Contenu scrollable */}
        <div className="overflow-y-auto flex-1">
          <form onSubmit={handleSubmit} className="p-6 space-y-6">
            {/* Informations de base */}
            <div className="bg-slate-800/50 rounded-lg p-4">
              <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
                <Package className="w-5 h-5 text-blue-400" />
                Informations de base
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-white font-semibold mb-2">Nom *</label>
                  <input
                    type="text"
                    value={formData.nom}
                    onChange={(e) => setFormData(prev => ({ ...prev, nom: e.target.value }))}
                    className="w-full bg-slate-700 border border-slate-600 rounded-lg px-4 py-2 text-white focus:ring-2 focus:ring-purple-500"
                    placeholder="Nom de l'outil..."
                    required
                  />
                </div>

                <div>
                  <label className="block text-white font-semibold mb-2">Référence</label>
                  <input
                    type="text"
                    value={formData.reference}
                    onChange={(e) => setFormData(prev => ({ ...prev, reference: e.target.value }))}
                    className="w-full bg-slate-700 border border-slate-600 rounded-lg px-4 py-2 text-white focus:ring-2 focus:ring-purple-500"
                  />
                </div>

                <div>
                  <label className="block text-white font-semibold mb-2">Catégorie *</label>
                  <select
                    value={formData.categorie_id}
                    onChange={(e) => setFormData(prev => ({ ...prev, categorie_id: e.target.value }))}
                    className="w-full bg-slate-700 border border-slate-600 rounded-lg px-4 py-2 text-white focus:ring-2 focus:ring-purple-500"
                    required
                  >
                    <option value="">Sélectionner...</option>
                    {categories.map(cat => (
                      <option key={cat.id} value={cat.id}>{cat.nom}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="flex items-center gap-2 text-white font-semibold mb-2">
                    <MapPin className="w-4 h-4" />
                    Emplacement *
                  </label>
                  <select
                    value={formData.emplacement_id}
                    onChange={(e) => setFormData(prev => ({ ...prev, emplacement_id: e.target.value }))}
                    className="w-full bg-slate-700 border border-slate-600 rounded-lg px-4 py-2 text-white focus:ring-2 focus:ring-purple-500"
                    required
                  >
                    <option value="">Sélectionner...</option>
                    {Object.entries(emplacementsGroupes).map(([type, emps]) => (
                      <optgroup key={type} label={typeLabels[type] || type}>
                        {emps.map(emp => (
                          <option key={emp.id} value={emp.id}>
                            {emp.atelier_code} - {emp.nom}
                          </option>
                        ))}
                      </optgroup>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-white font-semibold mb-2">Quantité</label>
                  <input
                    type="number"
                    min="1"
                    value={formData.quantite}
                    onChange={(e) => setFormData(prev => ({ ...prev, quantite: e.target.value }))}
                    className="w-full bg-slate-700 border border-slate-600 rounded-lg px-4 py-2 text-white focus:ring-2 focus:ring-purple-500"
                  />
                </div>

                <div>
                  <label className="block text-white font-semibold mb-2">État</label>
                  <select
                    value={formData.etat}
                    onChange={(e) => setFormData(prev => ({ ...prev, etat: e.target.value }))}
                    className="w-full bg-slate-700 border border-slate-600 rounded-lg px-4 py-2 text-white focus:ring-2 focus:ring-purple-500"
                  >
                    <option value="complet">Complet</option>
                    <option value="incomplet">Incomplet</option>
                  </select>
                </div>

                <div className="md:col-span-2">
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.disponible}
                      onChange={(e) => setFormData(prev => ({ ...prev, disponible: e.target.checked }))}
                      className="w-5 h-5 rounded text-purple-600"
                    />
                    <span className="text-white font-semibold">Disponible</span>
                  </label>
                </div>
              </div>
            </div>

            {/* Maintenance & Contrôles */}
            <div className="bg-orange-900/20 border border-orange-700/50 rounded-lg p-4">
              <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-orange-400" />
                Maintenance & Contrôles
              </h3>

              {/* Contrôles périodiques */}
              <div className="mb-4">
                <label className="flex items-center gap-3 cursor-pointer mb-3">
                  <input
                    type="checkbox"
                    checked={formData.necessite_controle}
                    onChange={(e) => setFormData(prev => ({ 
                      ...prev, 
                      necessite_controle: e.target.checked
                    }))}
                    className="w-5 h-5 rounded text-orange-600"
                  />
                  <span className="text-white font-semibold">Contrôles périodiques</span>
                </label>

                <AnimatePresence>
                  {formData.necessite_controle && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      className="grid grid-cols-1 md:grid-cols-2 gap-4 ml-8"
                    >
                      <div>
                        <label className="block text-gray-300 text-sm mb-2">Périodicité (mois) *</label>
                        <select
                          value={formData.periodicite_controle_mois}
                          onChange={(e) => setFormData(prev => ({ ...prev, periodicite_controle_mois: e.target.value }))}
                          className="w-full bg-slate-700 border border-slate-600 rounded-lg px-4 py-2 text-white focus:ring-2 focus:ring-orange-500"
                          required={formData.necessite_controle}
                        >
                          <option value="">Sélectionner...</option>
                          <option value="3">3 mois</option>
                          <option value="6">6 mois</option>
                          <option value="12">12 mois (annuel)</option>
                          <option value="24">24 mois</option>
                        </select>
                      </div>

                      <div>
                        <label className="flex items-center gap-2 text-gray-300 text-sm mb-2">
                          <Calendar className="w-4 h-4" />
                          Dernier contrôle
                        </label>
                        <input
                          type="date"
                          value={formData.date_dernier_controle}
                          onChange={(e) => setFormData(prev => ({ ...prev, date_dernier_controle: e.target.value }))}
                          className="w-full bg-slate-700 border border-slate-600 rounded-lg px-4 py-2 text-white focus:ring-2 focus:ring-orange-500"
                        />
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              {/* Remplacement obligatoire */}
              <div>
                <label className="flex items-center gap-3 cursor-pointer mb-3">
                  <input
                    type="checkbox"
                    checked={formData.necessite_remplacement}
                    onChange={(e) => setFormData(prev => ({ 
                      ...prev, 
                      necessite_remplacement: e.target.checked
                    }))}
                    className="w-5 h-5 rounded text-red-600"
                  />
                  <span className="text-white font-semibold">Remplacement obligatoire</span>
                </label>

                <AnimatePresence>
                  {formData.necessite_remplacement && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      className="grid grid-cols-1 md:grid-cols-2 gap-4 ml-8"
                    >
                      <div>
                        <label className="flex items-center gap-2 text-gray-300 text-sm mb-2">
                          <Clock className="w-4 h-4" />
                          Durée de vie (mois) *
                        </label>
                        <select
                          value={formData.duree_vie_mois}
                          onChange={(e) => setFormData(prev => ({ ...prev, duree_vie_mois: e.target.value }))}
                          className="w-full bg-slate-700 border border-slate-600 rounded-lg px-4 py-2 text-white focus:ring-2 focus:ring-red-500"
                          required={formData.necessite_remplacement}
                        >
                          <option value="">Sélectionner...</option>
                          <option value="6">6 mois</option>
                          <option value="12">12 mois (1 an)</option>
                          <option value="24">24 mois (2 ans)</option>
                          <option value="36">36 mois (3 ans)</option>
                        </select>
                      </div>

                      <div>
                        <label className="flex items-center gap-2 text-gray-300 text-sm mb-2">
                          <Calendar className="w-4 h-4" />
                          Mise en service
                        </label>
                        <input
                          type="date"
                          value={formData.date_mise_en_service}
                          onChange={(e) => setFormData(prev => ({ ...prev, date_mise_en_service: e.target.value }))}
                          className="w-full bg-slate-700 border border-slate-600 rounded-lg px-4 py-2 text-white focus:ring-2 focus:ring-red-500"
                        />
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              {/* Observations maintenance */}
              {(formData.necessite_controle || formData.necessite_remplacement) && (
                <div className="mt-4">
                  <label className="block text-gray-300 text-sm mb-2">Observations maintenance</label>
                  <textarea
                    value={formData.observations_maintenance}
                    onChange={(e) => setFormData(prev => ({ ...prev, observations_maintenance: e.target.value }))}
                    className="w-full bg-slate-700 border border-slate-600 rounded-lg px-4 py-2 text-white focus:ring-2 focus:ring-orange-500"
                    rows="2"
                    placeholder="Contrôle obligatoire, organisme agréé..."
                  />
                </div>
              )}
            </div>

            {/* Photo */}
            <div className="bg-slate-800/50 rounded-lg p-4">
              <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
                <Camera className="w-5 h-5 text-green-400" />
                Photo
              </h3>

              <div className="flex flex-wrap gap-4">
                <button
                  type="button"
                  onClick={() => setShowCamera(true)}
                  className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg flex items-center gap-2"
                >
                  <Camera className="w-5 h-5" />
                  Prendre
                </button>

                <label className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg flex items-center gap-2 cursor-pointer">
                  <Upload className="w-5 h-5" />
                  Uploader
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleFileUpload}
                    className="hidden"
                    disabled={uploadingPhoto}
                  />
                </label>

                {uploadingPhoto && <span className="text-yellow-400">Upload...</span>}
              </div>

              {formData.photo_url && (
                <img
                  src={formData.photo_url}
                  alt="Aperçu"
                  className="mt-4 w-48 h-32 object-cover rounded-lg border-2 border-green-500"
                />
              )}
            </div>

            {/* Observations générales */}
            <div>
              <label className="block text-white font-semibold mb-2">Observations générales</label>
              <textarea
                value={formData.observations}
                onChange={(e) => setFormData(prev => ({ ...prev, observations: e.target.value }))}
                className="w-full bg-slate-700 border border-slate-600 rounded-lg px-4 py-2 text-white focus:ring-2 focus:ring-purple-500"
                rows="3"
                placeholder="Notes supplémentaires..."
              />
            </div>

            {/* Boutons */}
            <div className="flex gap-4">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 px-6 py-3 bg-slate-700 hover:bg-slate-600 text-white rounded-lg"
                disabled={loading}
              >
                Annuler
              </button>
              <button
                type="submit"
                disabled={loading}
                className="flex-1 px-6 py-3 bg-purple-600 hover:bg-purple-700 text-white rounded-lg flex items-center justify-center gap-2"
              >
                {loading ? 'Sauvegarde...' : (
                  <>
                    <Save className="w-5 h-5" />
                    Enregistrer
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </motion.div>

      {/* Caméra */}
      <AnimatePresence>
        {showCamera && (
          <CameraCapture
            onCapture={handlePhotoCapture}
            onClose={() => setShowCamera(false)}
          />
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default OutilForm;